import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import Modal from '../components/ui/Modal';
import { useToast } from '../components/ui/Toast';
import { useGame } from '../context/GameContext';
import {
  billingEnabled,
  getMembership,
  openCustomerPortal,
} from '../services/billingService';
import SafeIcon from '../components/common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const {
  FiArchive,
  FiCheckCircle,
  FiDownload,
  FiExternalLink,
  FiRefreshCw,
  FiUpload,
  FiUser,
} = FiIcons;

const BACKUP_VERSION = 1;
const ROLLBACK_KEY = 'mindgarden-restore-rollback-v1';
const ALLOWED_KEYS = [
  'mindgarden-game-v4',
  'mindgarden-profile-v1',
  'mindgarden-workspace-v1',
  'habitGarden.v2',
  'habitGarden.v3',
  'thoughtGarden.v1',
  'mindgarden-thoughts-v1',
  'mindgarden-greenhouse-v1',
  'weedList.v2',
  'mindgarden-weeds-v1',
  'mindgarden-planner-v1',
  'mindgarden-calendar-v1',
  'mindgarden-kanban-v1',
  'mindgarden_grove_v1',
  'mindgarden-focus-task-v1',
  'mindgarden-art-collections-v1',
  'mindgarden-training-cards-v1',
  'mindgarden-training-cards-v2',
  'mindgarden-onboarding',
  'mindgarden-theme',
];

const PREFERENCE_KEYS = new Set([
  'mindgarden-onboarding',
  'mindgarden-theme',
]);

function readAllowedData(currentGameState) {
  const data = ALLOWED_KEYS.reduce((data, key) => {
    const value = localStorage.getItem(key);
    if (value !== null) data[key] = value;
    return data;
  }, {});

  if (currentGameState) {
    data['mindgarden-game-v4'] = JSON.stringify(currentGameState);
  }

  return data;
}

function downloadJson(payload, fileName) {
  const dataBlob = new Blob(
    [JSON.stringify(payload, null, 2)],
    { type: 'application/json' }
  );
  const url = URL.createObjectURL(dataBlob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function parseStoredValue(raw) {
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function validateStoredValue(key, raw) {
  if (PREFERENCE_KEYS.has(key)) return true;

  const parsed = parseStoredValue(raw);
  if (parsed === null) return false;

  if (key === 'mindgarden-game-v4') {
    return (
      parsed
      && typeof parsed === 'object'
      && !Array.isArray(parsed)
      && Array.isArray(parsed.gallery || [])
    );
  }

  if (key === 'mindgarden-workspace-v1') {
    return (
      parsed
      && typeof parsed === 'object'
      && !Array.isArray(parsed)
      && Array.isArray(parsed.tasks)
      && Array.isArray(parsed.notes)
    );
  }

  if (key === 'habitGarden.v2' || key === 'habitGarden.v3') {
    return (
      Array.isArray(parsed)
      || (
        parsed
        && typeof parsed === 'object'
        && Array.isArray(parsed.habits)
      )
    );
  }

  if (key === 'mindgarden-art-collections-v1') {
    return (
      Array.isArray(parsed)
      || (
        parsed
        && typeof parsed === 'object'
        && Array.isArray(parsed.items)
      )
    );
  }

  return typeof parsed === 'object';
}

function validateBackup(value) {
  if (
    !value
    || typeof value !== 'object'
    || value.product !== 'MindGarden'
    || value.backupVersion !== BACKUP_VERSION
    || !value.data
    || typeof value.data !== 'object'
    || Array.isArray(value.data)
  ) {
    throw new Error('This is not a supported MindGarden backup.');
  }

  const recognized = Object.entries(value.data).filter(
    ([key, raw]) => ALLOWED_KEYS.includes(key) && typeof raw === 'string'
  );

  if (!recognized.length) {
    throw new Error('The backup contains no recognized app data.');
  }

  recognized.forEach(([key, raw]) => {
    if (!validateStoredValue(key, raw)) {
      throw new Error(`The backup section "${key}" has an invalid shape.`);
    }
  });
  return Object.fromEntries(recognized);
}

function summarizeBackup(data) {
  const parse = (key) => {
    try {
      return JSON.parse(data[key] || 'null');
    } catch {
      return null;
    }
  };
  const game = parse('mindgarden-game-v4');
  const workspace = parse('mindgarden-workspace-v1');
  const habits = parse('habitGarden.v3') || parse('habitGarden.v2');
  const collections = parse('mindgarden-art-collections-v1');

  return {
    sections: Object.keys(data).length,
    tasks: Array.isArray(workspace?.tasks) ? workspace.tasks.length : 0,
    notes: Array.isArray(workspace?.notes) ? workspace.notes.length : 0,
    paintings: Array.isArray(game?.gallery) ? game.gallery.length : 0,
    habits: Array.isArray(habits?.habits)
      ? habits.habits.length
      : Array.isArray(habits)
        ? habits.length
        : 0,
    collections: Array.isArray(collections?.items)
      ? collections.items.length
      : Array.isArray(collections)
        ? collections.length
        : 0,
  };
}

export default function Settings() {
  const { state, actions } = useGame();
  const { success, error } = useToast();
  const displayNameRef = useRef(null);
  const fileRef = useRef(null);
  const backupUrlRef = useRef('');
  const [membership, setMembership] = useState({
    plan: 'free',
    verified: false,
  });
  const [billingBusy, setBillingBusy] = useState(false);
  const [preparedBackup, setPreparedBackup] = useState(null);
  const [restoreReview, setRestoreReview] = useState(null);
  const [showRestoreModal, setShowRestoreModal] = useState(false);
  const [showRecoveryModal, setShowRecoveryModal] = useState(false);
  const [recoveryInfo, setRecoveryInfo] = useState(null);

  useEffect(() => {
    let mounted = true;
    getMembership().then((result) => {
      if (mounted && result.ok) setMembership(result.membership);
    });
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => () => {
    if (backupUrlRef.current) {
      URL.revokeObjectURL(backupUrlRef.current);
    }
  }, []);

  const saveDisplayName = (event) => {
    event.preventDefault();
    const name = displayNameRef.current?.value.trim();

    if (!name) {
      error('Enter a display name.');
      displayNameRef.current?.focus();
      return;
    }

    actions.updateProfile({ ...(state.profile || {}), name });
    success('Display name saved on this browser.');
  };

  const handleExportData = () => {
    try {
      const data = readAllowedData(state);
      const payload = {
        product: 'MindGarden',
        backupVersion: BACKUP_VERSION,
        exportedAt: new Date().toISOString(),
        data,
      };
      const dataBlob = new Blob(
        [JSON.stringify(payload, null, 2)],
        { type: 'application/json' }
      );
      const url = URL.createObjectURL(dataBlob);

      if (backupUrlRef.current) {
        URL.revokeObjectURL(backupUrlRef.current);
      }
      backupUrlRef.current = url;

      setPreparedBackup({
        url,
        fileName: `mindgarden-backup-${new Date().toISOString().split('T')[0]}.json`,
        summary: summarizeBackup(data),
      });
      success(`Backup prepared with ${Object.keys(data).length} saved data sections.`);
    } catch (err) {
      console.error('Failed to export data:', err);
      error('Failed to export data. Please try again.');
    }
  };

  const handleImportData = () => {
    fileRef.current?.click();
  };

  const inspectImportData = (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    const reader = new FileReader();
    reader.onload = () => {
      try {
        const parsed = JSON.parse(String(reader.result));
        const data = validateBackup(parsed);
        setRestoreReview({
          data,
          exportedAt: parsed.exportedAt || '',
          summary: summarizeBackup(data),
        });
        setShowRestoreModal(true);
      } catch (err) {
        console.error('Failed to import data:', err);
        error('Failed to import data. Please check the file format.');
      }
    };
    reader.onerror = () => {
      error('Failed to import data. Please check the file format.');
    };
    reader.readAsText(file);
  };

  const confirmRestore = () => {
    if (!restoreReview?.data) return;

    try {
      const rollback = {
        product: 'MindGarden',
        backupVersion: BACKUP_VERSION,
        exportedAt: new Date().toISOString(),
        purpose: 'rollback-before-restore',
        data: readAllowedData(state),
      };

      localStorage.setItem(ROLLBACK_KEY, JSON.stringify(rollback));
      downloadJson(
        rollback,
        `mindgarden-rollback-${new Date().toISOString().split('T')[0]}.json`
      );

      Object.entries(restoreReview.data).forEach(([key, value]) => {
        localStorage.setItem(key, value);
      });

      success('Data imported successfully! The page will reload.');
      setShowRestoreModal(false);
      window.setTimeout(() => window.location.reload(), 500);
    } catch (err) {
      console.error('Failed to import data:', err);
      error('Failed to import data. Please check the file format.');
    }
  };

  const handleCreateBackup = () => {
    try {
      actions.createBackup();
      success('Emergency backup created successfully!');
    } catch (err) {
      error('Failed to create backup.');
    }
  };

  const handleShowRecovery = () => {
    const info = actions.getRecoveryInfo();
    setRecoveryInfo(info);
    setShowRecoveryModal(true);
  };

  const handleForceRecovery = () => {
    try {
      if (actions.forceRecovery()) {
        success('Recovery successful! Your data has been restored.');
        setShowRecoveryModal(false);
      } else {
        error('No recoverable data found.');
      }
    } catch (err) {
      error('Recovery failed. Please try importing a backup file.');
    }
  };

  const manageMembership = async () => {
    setBillingBusy(true);
    const result = await openCustomerPortal();
    setBillingBusy(false);

    if (result.ok) {
      window.location.assign(result.portalUrl);
    } else {
      error(result.message || 'Membership management is unavailable.');
    }
  };

  const verifiedPlus =
    membership.verified === true && membership.plan === 'plus';

  return (
    <div className="mx-auto max-w-5xl space-y-7">
      <header>
        <p className="mb-2 text-xs font-bold uppercase tracking-[0.2em] text-sage">
          Settings
        </p>
        <h1 className="font-cormorant text-4xl font-bold text-pine lg:text-5xl">
          Your studio, your data.
        </h1>
        <p className="mt-2 max-w-2xl text-pine/65">
          Personalize your workspace and keep a portable copy of the work saved in this browser.
        </p>
      </header>

      <div className="grid gap-5 lg:grid-cols-[0.8fr_1.2fr]">
        <div className="space-y-5">
          <Card className="p-6">
            <div className="mb-5 flex items-center gap-3">
              <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-sage/15">
                <SafeIcon icon={FiUser} className="text-pine" />
              </span>
              <div>
                <h2 className="font-cormorant text-2xl font-bold text-pine">
                  Studio profile
                </h2>
                <p className="text-xs text-pine/50">Saved on this browser</p>
              </div>
            </div>

            <form onSubmit={saveDisplayName}>
              <label
                htmlFor="display-name"
                className="mb-2 block text-sm font-bold text-pine"
              >
                Display name
              </label>
              <input
                id="display-name"
                ref={displayNameRef}
                required
                autoComplete="name"
                defaultValue={state.profile?.name || ''}
                className="w-full rounded-xl border border-pine/15 bg-paper px-4 py-3 text-pine focus:outline-none focus:ring-2 focus:ring-sage"
              />
              <Button type="submit" className="mt-4">
                Save name
              </Button>
            </form>
          </Card>

          <Card className="p-6">
            <h2 className="font-cormorant text-2xl font-bold text-pine">
              Membership
            </h2>
            <div className="mt-4 rounded-2xl bg-paper p-4">
              <p className="text-xs font-bold uppercase tracking-wider text-pine/45">
                Current access
              </p>
              <p className="mt-1 text-xl font-bold text-pine">
                {verifiedPlus ? 'Studio Plus' : 'Free access'}
              </p>
            </div>

            <div className="mt-4 flex flex-wrap gap-2">
              <Button as={Link} to="/membership" variant="secondary">
                View membership
              </Button>
              {verifiedPlus && (
                <Button
                  variant="secondary"
                  onClick={manageMembership}
                  disabled={billingBusy}
                  icon={FiExternalLink}
                >
                  {billingBusy ? 'Opening…' : 'Manage membership'}
                </Button>
              )}
            </div>

            {!billingEnabled && (
              <p className="mt-3 text-xs text-pine/50">
                Studio Plus purchases and account management are not configured yet.
              </p>
            )}
          </Card>
        </div>

        <Card className="p-6">
          <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-start">
            <div>
              <h2 className="font-cormorant text-2xl font-bold text-pine">
                Backup and restore
              </h2>
              <p className="mt-1 max-w-xl text-sm leading-relaxed text-pine/60">
                Backups are free. Export creates a JSON copy of recognized
                MindGarden data stored in this browser. Authentication,
                credentials, secrets, and billing session tokens are excluded.
              </p>
            </div>
            <span className="inline-flex items-center gap-2 whitespace-nowrap rounded-full bg-sage/15 px-3 py-1.5 text-xs font-bold text-pine">
              <SafeIcon icon={FiCheckCircle} />
              Saved on this browser
            </span>
          </div>

          <div className="mt-6 grid gap-3 sm:grid-cols-2">
            <button
              type="button"
              onClick={handleExportData}
              className="rounded-2xl border border-pine/10 bg-paper p-5 text-left transition hover:border-pine/25 focus:outline-none focus:ring-2 focus:ring-sage"
            >
              <SafeIcon icon={FiDownload} className="mb-4 h-5 w-5 text-sage" />
              <strong className="block text-pine">Export complete backup</strong>
              <span className="mt-1 block text-xs text-pine/55">
                Download workspace, paintings, habits, journal, planning, and classic progress.
              </span>
            </button>

            <button
              type="button"
              onClick={handleImportData}
              className="rounded-2xl border border-pine/10 bg-paper p-5 text-left transition hover:border-pine/25 focus:outline-none focus:ring-2 focus:ring-sage"
            >
              <SafeIcon icon={FiUpload} className="mb-4 h-5 w-5 text-goldenrod" />
              <strong className="block text-pine">Review a backup</strong>
              <span className="mt-1 block text-xs text-pine/55">
                Validate its contents before replacing recognized data.
              </span>
            </button>

            <input
              ref={fileRef}
              type="file"
              accept="application/json,.json"
              onChange={inspectImportData}
              className="sr-only"
              aria-label="Choose MindGarden backup"
            />
          </div>

          {preparedBackup && (
            <div className="mt-5 rounded-2xl border border-sage/30 bg-sage/10 p-4">
              <strong className="block text-pine">Backup ready</strong>
              <p className="mt-1 text-sm text-pine/60">
                {preparedBackup.summary.paintings} paintings ·{' '}
                {preparedBackup.summary.tasks} tasks ·{' '}
                {preparedBackup.summary.notes} journal entries ·{' '}
                {preparedBackup.summary.habits} habits
              </p>
              <a
                href={preparedBackup.url}
                download={preparedBackup.fileName}
                className="mt-3 inline-flex min-h-11 items-center gap-2 rounded-xl bg-pine px-4 py-2.5 text-sm font-bold text-paper focus:outline-none focus:ring-2 focus:ring-sage"
              >
                <SafeIcon icon={FiDownload} />
                Save backup
              </a>
            </div>
          )}

          <div className="mt-6 flex flex-wrap gap-4 border-t border-pine/10 pt-5">
            <button
              type="button"
              onClick={handleCreateBackup}
              className="inline-flex items-center gap-2 text-sm font-bold text-pine/60 hover:text-pine"
            >
              <SafeIcon icon={FiArchive} />
              Create classic emergency backup
            </button>
            <button
              type="button"
              onClick={handleShowRecovery}
              className="inline-flex items-center gap-2 text-sm font-bold text-pine/60 hover:text-pine"
            >
              <SafeIcon icon={FiRefreshCw} />
              Open trusted recovery tools
            </button>
          </div>
        </Card>
      </div>

      <Card className="p-6">
        <div className="flex items-center gap-3">
          <SafeIcon icon={FiArchive} className="text-sage" />
          <h2 className="font-cormorant text-2xl font-bold text-pine">
            Classic workspace
          </h2>
        </div>
        <p className="mt-2 text-sm text-pine/60">
          Earlier tools and progress remain available without crowding the main studio.
        </p>
        <nav
          aria-label="Classic MindGarden tools"
          className="mt-4 flex flex-wrap gap-2"
        >
          {[
            ['/classic/garden', 'Classic garden'],
            ['/planner', 'Old planner'],
            ['/calendar', 'Calendar'],
            ['/the-grove', 'The Grove'],
          ].map(([to, label]) => (
            <Link
              key={to}
              to={to}
              className="rounded-full border border-pine/10 bg-paper px-4 py-2 text-sm font-bold text-pine transition hover:border-pine/30"
            >
              {label}
            </Link>
          ))}
        </nav>
      </Card>

      <Modal
        isOpen={showRestoreModal}
        onClose={() => {
          setShowRestoreModal(false);
          setRestoreReview(null);
        }}
        title="Review backup"
        size="lg"
      >
        {restoreReview && (
          <div className="space-y-5 p-6">
            <p className="text-sm text-pine/65">
              Only recognized MindGarden data will be restored. A rollback
              backup will be saved and downloaded before any value is replaced.
            </p>

            <dl className="grid grid-cols-2 gap-3 sm:grid-cols-3">
              {[
                ['Data sections', restoreReview.summary.sections],
                ['Tasks', restoreReview.summary.tasks],
                ['Journal entries', restoreReview.summary.notes],
                ['Paintings', restoreReview.summary.paintings],
                ['Habits', restoreReview.summary.habits],
                ['Collections', restoreReview.summary.collections],
              ].map(([label, value]) => (
                <div key={label} className="rounded-xl bg-paper p-3">
                  <dt className="text-xs text-pine/50">{label}</dt>
                  <dd className="text-xl font-bold text-pine">{value}</dd>
                </div>
              ))}
            </dl>

            {restoreReview.exportedAt && (
              <p className="text-xs text-pine/50">
                Exported {new Date(restoreReview.exportedAt).toLocaleString()}
              </p>
            )}

            <div className="flex justify-end gap-2">
              <Button
                variant="secondary"
                onClick={() => {
                  setShowRestoreModal(false);
                  setRestoreReview(null);
                }}
              >
                Cancel
              </Button>
              <Button onClick={confirmRestore}>
                Replace recognized data
              </Button>
            </div>
          </div>
        )}
      </Modal>

      <Modal
        isOpen={showRecoveryModal}
        onClose={() => setShowRecoveryModal(false)}
        title="Classic progress recovery"
        size="lg"
      >
        <div className="space-y-5 p-6">
          <p className="text-sm text-pine/65">
            These existing recovery layers apply to the classic game save only.
          </p>

          {recoveryInfo && (
            <dl className="grid grid-cols-2 gap-3">
              <div className="rounded-xl bg-paper p-3">
                <dt className="text-xs text-pine/50">Primary save</dt>
                <dd className="font-bold text-pine">
                  {recoveryInfo.primaryState ? 'Available' : 'Not found'}
                </dd>
              </div>
              <div className="rounded-xl bg-paper p-3">
                <dt className="text-xs text-pine/50">Local recovery copies</dt>
                <dd className="font-bold text-pine">
                  {Object.values(recoveryInfo.backups || {}).filter(Boolean).length}
                </dd>
              </div>
            </dl>
          )}

          <div className="flex justify-end gap-2">
            <Button
              variant="secondary"
              onClick={() => setShowRecoveryModal(false)}
            >
              Close
            </Button>
            <Button onClick={handleForceRecovery} icon={FiRefreshCw}>
              Recover classic progress
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}